/* eslint-disable react/prop-types */
import React, {
	useState,
	useImperativeHandle,
	forwardRef,
	useRef,
	useEffect,
	useCallback,
	FC,
} from "react";
import PropTypes from "prop-types";
import { AgGridReact } from "ag-grid-react";
import GridCheckbox from "../../GridCheckbox";
import { MDBModal } from "mdbreact";
import styles from "./StyledMultiModalSelector.module.css";
import classNames from "classnames";
import { createDynamicColumnsFromObject } from "../../../utils/grid";
import CloseIcon from "../../../assets/close-cancel.svg";
import { Button, Input, Modal } from "antd";
import DefaultInput from "../DefaultInput";

interface IMultiModalSelector {
	tableData: any[];
	onRowsSelected: (rows: any) => void;
	onRowsCleared: () => void;
	buttonLabel: string;
	primaryKey: string;
	buttonDesc?: string;
	buttonDescAll?: string;
	selectorClassName?: string;
	disabled?: boolean;
	value?: any;
	ref?: any;
	inputName?: string;
	id?: any;
}
const MultiModalSelector: FC<IMultiModalSelector> = forwardRef(
	(
		{
			tableData,
			onRowsSelected,
			onRowsCleared,
			buttonLabel,
			primaryKey = "id",
			buttonDesc = "Zgjedh",
			buttonDescAll = "Te gjitha",
			selectorClassName = "",
			disabled = false,
			value,
			inputName,
		},
		ref
	) => {
		const [isOpen, setIsOpen] = useState<boolean>(false);
		const [buttonDescription, setButtonDescription] = useState<string>("");
		const [columns, setColumns] = useState<any[]>([]);
		const [selectedRows, setSelectedRows] = useState(new Set());
		const gridRef = useRef<any>();

		useImperativeHandle(ref, () => ({
			clear: () => {
				setButtonDescription("");
				setSelectedRows(new Set());
			},
			setRows: (rows: any) => {
				if (rows?.length > 0) {
					setSelectedRows(new Set(rows));
					if (rows.length === tableData.length) {
						setButtonDescription(buttonDescAll);
						return;
					} else {
						setButtonDescription(rows.length);
					}
				} else {
					setButtonDescription("");
					setSelectedRows(new Set());
				}
			},
		}));

		const toggleIsOpen = () => {
			setIsOpen(!isOpen);
		};

		const onFilterTextBoxChanged = useCallback((value: any) => {
			gridRef?.current.api.setQuickFilter(value);
		}, []);

		const submitHandler = async () => {
			let selectedRows = gridRef.current?.api?.getSelectedRows();
			setSelectedRows(
				new Set(selectedRows.map((element: any) => element[primaryKey]))
			);
			if (selectedRows.length) {
				if (selectedRows.length === tableData.length) {
					onRowsSelected?.(selectedRows);
					setButtonDescription(buttonDescAll);
					toggleIsOpen();
					return;
				}
				onRowsSelected?.(selectedRows);
				setButtonDescription(selectedRows.length);
			} else {
				onRowsCleared?.();
				setButtonDescription("");
			}
			toggleIsOpen();
		};

		useEffect(() => {
			if (tableData?.length) {
				const columns = createDynamicColumnsFromObject(tableData?.[0]);
				const selected = [
					{
						field: "selected",
						headerCheckboxSelection: true,
						checkboxSelection: true,
					},
					...columns,
				];
				setColumns(selected);
			}
		}, [tableData]);

		const onFirstDataRendered = useCallback(
			(params: any) => {
				params.api?.forEachNode((node: any) => {
					node.setSelected(selectedRows.has(node.data[primaryKey]));
				});
			},
			[isOpen, selectedRows]
		);
		useEffect(() => {
			if (value?.length && value?.length === tableData?.length) {
				setButtonDescription(buttonDescAll);
			} else if (value?.length === 0) {
				setButtonDescription("");
				setSelectedRows(new Set());
			} else {
				setButtonDescription(value?.length?.toString());
			}
		}, [value]);
		return (
			<>
				<MDBModal
					animation="bottom"
					isOpen={isOpen}
					centered
					toggle={toggleIsOpen}
					className={styles.modal}
					contentClassName={styles.modalContent}
				>
					<div className={styles.titleContainer}>
						<div className={styles.title}>Zgjedh</div>
						<button onClick={toggleIsOpen}>
							<img
								draggable="false"
								className={styles.icon}
								src={CloseIcon}
								alt="close"
							/>
						</button>
					</div>
					<div className={styles.searchContainer}>
						<DefaultInput
							className={styles.searchInput}
							placeholder="Search..."
							onChange={(e) =>
								onFilterTextBoxChanged(e.target.value)
							}
						/>
					</div>
					<div
						className={classNames(
							"ag-theme-alpine ag-header-radius",
							styles.grid
						)}
					>
						<AgGridReact
							ref={gridRef}
							rowData={tableData}
							columnDefs={columns}
							defaultColDef={defaultColDef}
							rowSelection={"multiple"}
							rowMultiSelectWithClick
							components={frameworkComponents}
							onFirstDataRendered={onFirstDataRendered}
							cacheQuickFilter
							rowHeight={69}
							headerHeight={52}
						/>
					</div>
					<div className={styles.buttons}>
						<Button
							onClick={toggleIsOpen}
							className={styles.modalButton}
						>
							Close
						</Button>
						<Button
							onClick={submitHandler}
							className={styles.modalButton}
						>
							Submit
						</Button>
					</div>
				</MDBModal>

				<div
					className={classNames(
						styles.selectorContainer,
						selectorClassName
					)}
				>
					<div
						className={styles.label}
						//htmlFor={inputName ?? "selectorInput"}
					>
						{buttonLabel}
					</div>

					<button
						className={classNames(styles.button, {
							[styles.buttonHasValue]:
								!!buttonDescription && !disabled,
							[styles.disabled]: !!disabled,
						})}
						onClick={(event) => {
							!disabled && toggleIsOpen();
						}}
						type="button"
						disabled={disabled}
					>
						{buttonDescription ?? buttonDesc}
					</button>
				</div>
			</>
		);
	}
);

const defaultColDef = {
	flex: 1,
	minWidth: 100,
	sortable: true,
	filter: true,
	wrapHeaderText: true,
	resizable: true,
	filterParams: { excelMode: "windows" },
};

const frameworkComponents = { checkboxRenderer: GridCheckbox };
export default MultiModalSelector;
